import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Warning, CaretDown, CaretRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Beta-2F の確認画面（読み取り専用）。
 * 検出して知らせるだけで、分割・子書類の作成・番号の払い出しは一切行いません。
 */
export default function MixedDocNotice({ documentId, onResolved }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openSegments, setOpenSegments] = useState(false);

  const load = useCallback(async () => {
    if (!documentId) return;
    try {
      const r = await axios.get(`${API}/api/dms/mixed-review/${documentId}`,
        { headers: authHeader() });
      setState(r.data);
    } catch { setState(null); }
  }, [documentId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/dms/mixed-review/${documentId}/decision`,
        { decision }, { headers: authHeader() });
      setState(r.data);
      toast.success(r.data.message_ja);
      if (decision === 'open_review') setOpenSegments(true);
      if (decision === 'keep_single') onResolved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存できませんでした');
    } finally { setBusy(false); }
  };

  if (!state || !state.requires_review) return null;
  if (!state.show_notice && !openSegments) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 mb-4"
      data-testid="mixed-doc-notice">
      <div className="flex items-start gap-2.5">
        <Warning size={18} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-900" data-testid="mixed-doc-notice-text">
            このファイルには複数種類の書類が含まれている可能性があります。
          </p>
          <p className="text-xs text-amber-800/80 mt-1">
            確認のみのお知らせです。ファイルの分割や新しい書類の作成は行いません
            {state.segment_count > 0 && ` · ${state.segment_count}件の区切りを検出`}
          </p>
          {state.decision_stale_note_ja && (
            <p className="text-xs text-amber-900 mt-1" data-testid="mixed-doc-stale">
              {state.decision_stale_note_ja}
            </p>
          )}
          {state.decision_label_ja && (
            <p className="text-xs text-zinc-600 mt-1" data-testid="mixed-doc-decision">
              対応: {state.decision_label_ja}
              {state.decided_by ? `（${state.decided_by}）` : ''}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => decide('keep_single')}
              className="h-9 text-sm border-amber-300 bg-white"
              data-testid="mixed-doc-keep-single">1つの書類として扱う</Button>
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => decide('review_later')}
              className="h-9 text-sm border-amber-300 bg-white"
              data-testid="mixed-doc-review-later">後で確認する</Button>
            <Button size="sm" disabled={busy}
              onClick={() => { setOpenSegments((v) => !v); if (!openSegments) decide('open_review'); }}
              className="h-9 text-sm bg-teal-700 hover:bg-teal-800"
              aria-expanded={openSegments}
              data-testid="mixed-doc-open-review">
              {openSegments ? <CaretDown size={13} className="mr-1" /> : <CaretRight size={13} className="mr-1" />}
              内容を確認する
            </Button>
          </div>

          {openSegments && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-white p-3"
              data-testid="mixed-doc-segments">
              <p className="text-xs text-zinc-500 mb-2">
                {state.read_only_note_ja || 'この画面は確認のみです。'}
              </p>
              <ol className="space-y-1.5">
                {(state.segments || []).map((s) => (
                  <li key={s.segment_id || s.index}
                    data-testid={`mixed-doc-segment-${s.index}`}
                    className="flex items-start gap-2 text-sm text-zinc-800">
                    <span className="mt-0.5 inline-flex items-center justify-center h-5 min-w-5 rounded bg-zinc-100 px-1 text-xs font-semibold text-zinc-600 tabular-nums">
                      {s.index}
                    </span>
                    <span className="min-w-0">
                      <span className="block">
                        {s.document_type_label_ja || '種類は判定できません'}
                        {s.range_ja ? ` — ${s.range_ja}` : ''}
                      </span>
                      {(s.evidence_ja || []).length > 0 && (
                        <span className="block text-xs text-zinc-500">
                          根拠: {s.evidence_ja.filter(Boolean).join(' / ')}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
                {(state.segments || []).length === 0 && (
                  <li className="text-sm text-zinc-400">区切りの詳細はありません</li>
                )}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
